import {
  createPostgresWakeSignalBus,
  createPostgresWakeSignalListenerSource,
  type PostgresWakeSignalListenClient,
  type WakeSignalBus,
} from "./postgres-wake-signal-bus";
import type { WakeSignalDiagnosticsSnapshot } from "./wake-signal-diagnostics";
import { GENCOW_PLATFORM_WAKE_CHANNEL } from "./wake-signal-types";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "log" | "warn">;

type PostgresWakeSignalSqlClient = PostgresWakeSignalListenClient & {
  end(options?: { timeout?: number }): Promise<void>;
};

type PostgresWakeSignalFactory = (
  url: string,
  options: { max: number; idle_timeout: number; max_lifetime: number },
) => PostgresWakeSignalSqlClient;

export type PostgresWakeSignalRuntime = {
  bus: WakeSignalBus;
  getDiagnostics(): Promise<WakeSignalDiagnosticsSnapshot>;
  close(): Promise<void>;
};

export type PostgresWakeSignalRuntimeConfig =
  | { enabled: false; reason: "disabled" | "database_url_missing" }
  | { enabled: true; databaseUrl: string };

function isEnabled(value: string | undefined): boolean {
  return value === "1" || value === "true" || value === "yes" || value === "on";
}

function normalizeUrlParam(value: string | null): string {
  return (value ?? "").trim().toLowerCase();
}

function isTransactionPoolingUrl(databaseUrl: string): boolean {
  const url = new URL(databaseUrl);
  const pgbouncer = normalizeUrlParam(url.searchParams.get("pgbouncer"));
  const poolMode = normalizeUrlParam(url.searchParams.get("pool_mode"));
  const options = normalizeUrlParam(url.searchParams.get("options"));
  return (
    pgbouncer === "true" ||
    poolMode === "transaction" ||
    /\bpool_mode\s*=\s*transaction\b/.test(options)
  );
}

export function assertPostgresWakeSignalListenerUrl(databaseUrl: string): void {
  try {
    if (!isTransactionPoolingUrl(databaseUrl)) return;
  } catch {
    throw new Error("GENCOW_WAKE_SIGNAL_DATABASE_URL must be a valid PostgreSQL listener URL");
  }
  throw new Error(
    "GENCOW_WAKE_SIGNAL_DATABASE_URL must use direct PostgreSQL or session-pooling PgBouncer; transaction-pooling PgBouncer cannot LISTEN",
  );
}

export function resolvePostgresWakeSignalRuntimeConfig(
  env: Record<string, string | undefined> = process.env,
): PostgresWakeSignalRuntimeConfig {
  if (!isEnabled(env.GENCOW_WAKE_SIGNAL_ENABLED)) {
    return { enabled: false, reason: "disabled" };
  }

  const databaseUrl = env.GENCOW_WAKE_SIGNAL_DATABASE_URL?.trim();
  if (!databaseUrl) {
    return { enabled: false, reason: "database_url_missing" };
  }
  assertPostgresWakeSignalListenerUrl(databaseUrl);

  return { enabled: true, databaseUrl };
}

async function loadPostgresFactory(): Promise<PostgresWakeSignalFactory> {
  const mod = (await import("postgres")) as { default: PostgresWakeSignalFactory };
  return mod.default;
}

export async function createPostgresWakeSignalRuntime(params: {
  rawSql: RawSql;
  env?: Record<string, string | undefined>;
  logger?: LoggerLike;
  channel?: string;
  defaultChannel?: string;
  postgresFactory?: PostgresWakeSignalFactory;
}): Promise<PostgresWakeSignalRuntime | null> {
  const config = resolvePostgresWakeSignalRuntimeConfig(params.env);
  if (!config.enabled) {
    if (config.reason === "database_url_missing") {
      params.logger?.warn("[wake-signal] GENCOW_WAKE_SIGNAL_ENABLED is on but listener URL is missing");
    }
    return null;
  }

  const postgresFactory = params.postgresFactory ?? (await loadPostgresFactory());
  const client = postgresFactory(config.databaseUrl, {
    max: 1,
    idle_timeout: 0,
    max_lifetime: 0,
  });
  const bus = createPostgresWakeSignalBus({
    rawSql: params.rawSql,
    source: createPostgresWakeSignalListenerSource(client),
    channel: params.channel,
    defaultChannel: params.defaultChannel ?? GENCOW_PLATFORM_WAKE_CHANNEL,
    logger: params.logger,
  });
  params.logger?.log("[wake-signal] PostgreSQL listener runtime enabled");

  return {
    bus,
    getDiagnostics: () => bus.getDiagnostics(),
    close: () => client.end({ timeout: 5 }),
  };
}
